<template>
<v-container grid-list-xl fluid class="deliverable-container">
    <v-layout row wrap>
        <v-flex lg12>
            <div class="d-flex justify-space-between mb-4 flex-wrap" style="gap: 16px;">
                <div>
                    <h2 class="font-weight-bold mb-1">Kho VN — Tạo phiếu xuất kho</h2>
                    <div class="caption grey--text text--darken-1">
                        Gom theo khách hàng các kiện đã được KT duyệt xuất (`Deliverable = 1`, `Status = 4`, chưa có phiếu giao).
                    </div>
                </div>
                <div class="d-flex flex-wrap" style="gap: 12px;">
                    <v-card
                        class="pa-3 text-center stat-card cursor-pointer"
                        min-width="130"
                        :class="{ 'stat-card-active': filterMode === 'ship_requested' }"
                        @click="setFilterMode('ship_requested')"
                    >
                        <div class="caption grey--text text--darken-1">Yêu cầu ship</div>
                        <div class="title font-weight-bold red--text text--darken-1">{{ summaryStats.shipRequestedCustomers }}</div>
                    </v-card>
                    <v-card
                        class="pa-3 text-center stat-card cursor-pointer"
                        min-width="130"
                        :class="{ 'stat-card-active': filterMode === 'all' }"
                        @click="setFilterMode('all')"
                    >
                        <div class="caption grey--text text--darken-1">Khách chờ xuất</div>
                        <div class="title font-weight-bold primary--text">{{ summaryStats.customers }}</div>
                    </v-card>
                    <v-card class="pa-3 text-center stat-card" min-width="130">
                        <div class="caption grey--text text--darken-1">Kiện chờ xuất</div>
                        <div class="title font-weight-bold orange--text text--darken-2">{{ summaryStats.packages }}</div>
                    </v-card>
                    <v-card class="pa-3 text-center stat-card" min-width="130">
                        <div class="caption grey--text text--darken-1">Vận đơn</div>
                        <div class="title font-weight-bold success--text">{{ summaryStats.transactions }}</div>
                    </v-card>
                </div>
            </div>

            <v-card>
                <div class="pa-4 deliverable-toolbar">
                    <v-layout row wrap align-center>
                        <v-flex lg4 md6 sm12 class="pr-md-2">
                            <v-text-field
                                v-model="searchQuery"
                                label="Tìm mã VĐ / mã khách / đơn hàng..."
                                outlined
                                dense
                                hide-details
                                clearable
                                append-icon="fa-search"
                            ></v-text-field>
                        </v-flex>
                        <v-flex lg3 md6 sm12 class="pr-md-2">
                            <v-autocomplete
                                :items="customerFilterItems"
                                item-text="text"
                                item-value="value"
                                v-model="selectedCustomerId"
                                label="Lọc theo khách hàng"
                                outlined
                                dense
                                hide-details
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex lg5 md12 sm12 class="text-right deliverable-toolbar-actions">
                            <v-btn
                                v-if="canOperate"
                                color="success"
                                class="text-none"
                                :disabled="filteredGroupedDeliverables.length === 0 || isBusy || !hasAnyValidReceiptGroup"
                                @click="openAllReceiptDialog"
                            >
                                <v-icon left small>fa-file-invoice</v-icon>
                                Tạo phiếu toàn bộ
                            </v-btn>
                            <v-btn color="primary" class="text-none ml-2" :loading="isBusy" @click="refreshData">
                                <v-icon left small>fa-sync</v-icon>
                                Tải lại
                            </v-btn>
                        </v-flex>
                    </v-layout>
                </div>

                <div v-if="canOperate && selectedGroups.length > 0" class="selection-bar pa-3 d-flex align-center flex-wrap">
                    <v-icon color="primary" size="16" class="mr-2">fa-check-square</v-icon>
                    <span class="selection-text">
                        Đã chọn <b>{{ selectedSummary.customers }} khách</b> · {{ selectedSummary.packages }} kiện · {{ selectedSummary.transactions }} vận đơn
                    </span>
                    <span class="selection-text ml-2">
                        · {{ selectedSummary.weight | currency('', 2) }} kg · {{ selectedSummary.volume | currency('', 2) }} CBM
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="success"
                        class="text-none"
                        small
                        :disabled="isBusy"
                        @click="openSelectedReceiptDialog"
                    >
                        <v-icon left small>fa-file-invoice</v-icon>
                        Tạo phiếu giao
                    </v-btn>
                    <v-btn icon small class="ml-2" @click="selectedCustomerUIDs = []">
                        <v-icon size="14" color="grey">fa-times</v-icon>
                    </v-btn>
                </div>

                <div class="deliverable-table">
                    <div class="deliverable-table-header">
                        <div class="col-select" v-if="canOperate">
                            <input
                                type="checkbox"
                                :checked="isAllSelected"
                                :disabled="filteredGroupedDeliverables.length === 0"
                                @change="toggleSelectAll($event.target.checked)"
                            >
                        </div>
                        <div class="col-customer">Khách hàng</div>
                        <div class="col-packages">Đơn · Mã VĐ · Kiện · Kg</div>
                        <div class="col-finance">Thanh toán</div>
                        <div class="col-ship-note">Ghi chú Giao hàng</div>
                        <div class="col-note">Ghi chú kho</div>
                        <div class="col-action" v-if="canOperate">Tạo phiếu</div>
                    </div>

                    <div v-if="isBusy && groupedDeliverables.length === 0" class="pa-10 text-center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <div class="mt-2 grey--text">Đang tải dữ liệu...</div>
                    </div>

                    <div v-else-if="filteredGroupedDeliverables.length === 0" class="pa-10 text-center grey--text">
                        Không có kiện nào chờ xuất.
                    </div>

                    <div v-else v-for="group in filteredGroupedDeliverables" :key="group.UID" class="group-row">
                        <div class="col-select" v-if="canOperate">
                            <input
                                type="checkbox"
                                :checked="isGroupSelected(group.UID)"
                                @change="toggleSelectGroup(group.UID, $event.target.checked)"
                            >
                        </div>

                        <div class="col-customer">
                            <div class="d-flex align-center mb-1">
                                <v-icon small color="primary" class="mr-2">fa-user</v-icon>
                                <span class="font-weight-bold">{{ getCustomerFullName(group.UID) }}</span>
                            </div>
                            <div class="caption grey--text text--darken-2 mb-1 d-flex align-center flex-wrap">
                                <span>{{ getCustomerAddress(group.UID) || 'Chưa có địa chỉ' }}</span>
                                <v-tooltip top v-if="canViewCustomerPhone">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            x-small
                                            color="primary"
                                            class="ml-1"
                                            style="width: 18px; height: 18px; min-width: 18px;"
                                            v-bind="attrs"
                                            v-on="on"
                                            @click.stop="toggleShowPhone(group.UID)"
                                        >
                                            <v-icon size="13">fa-question-circle</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>SĐT: {{ getCustomerPhone(group.UID) }} (Click để hiện/ẩn)</span>
                                </v-tooltip>
                            </div>
                            <div v-if="showPhoneMap[group.UID]" class="caption primary--text font-weight-bold mb-1">
                                <v-icon x-small color="primary" class="mr-1">fa-phone</v-icon>
                                SĐT: {{ getCustomerPhone(group.UID) }}
                            </div>
                            <div class="caption grey--text text--darken-1 mb-1">
                                KH: {{ group.UserCode }}
                            </div>
                            <div class="caption grey--text text--darken-1">
                                {{ group.orders.length }} đơn · {{ group.totalPackages || 0 }} kiện
                            </div>

                            <div v-if="group.hasShipRequest" class="mt-2 mb-1 ship-request-info" :class="{ 'cursor-pointer': canOperate }" @click="canOperate && openShipRequestDialog(group)">
                                <div class="caption font-weight-bold red--text text--darken-2 d-flex align-center">
                                    <span class="mr-1">•</span> Yêu cầu ship
                                </div>
                                <div class="caption grey--text text--darken-2 font-weight-medium">
                                    • {{ group.shipRequestDate }}
                                </div>
                            </div>
                            <div v-else-if="canOperate" class="mt-2 mb-1">
                                <v-btn
                                    x-small
                                    color="primary"
                                    depressed
                                    class="text-none font-weight-bold"
                                    @click="openShipRequestDialog(group)"
                                >
                                    <v-icon left size="10">fa-shipping-fast</v-icon>
                                    Yêu cầu ship
                                </v-btn>
                            </div>

                            <v-chip
                                small
                                label
                                class="mt-1"
                                :color="canCreateReceiptGroup(group) ? 'green lighten-5' : 'red lighten-5'"
                                :text-color="canCreateReceiptGroup(group) ? 'green darken-2' : 'red darken-2'"
                            >
                                {{ canCreateReceiptGroup(group) ? 'Được duyệt tài chính' : 'Chưa duyệt tài chính' }}
                            </v-chip>
                        </div>

                        <div class="col-packages">
                            <div class="package-summary mb-2">
                                <span class="summary-pill">{{ group.orders.length }} đơn</span>
                                <span class="summary-pill">{{ group.totalPackages || 0 }} kiện</span>
                                <span class="summary-pill">{{ group.Weight | currency('', 2) }} kg</span>
                                <span class="summary-pill">{{ group.Volume | currency('', 2) }} CBM</span>
                            </div>
                            <div v-for="pkg in group.packages" :key="pkg.ID" class="package-item">
                                <div class="package-code">{{ pkg.TransactionCode }}</div>
                                <div class="package-meta">
                                    <a :href="getPackageOrderLink(pkg)" target="_blank">{{ getPackageOrderLabel(pkg) }}</a> · {{ pkg.NumberOfPack || 1 }} kiện · {{ pkg.Weight | currency('', 2) }} kg · {{ pkg.Volume | currency('', 2) }} CBM
                                </div>
                            </div>
                        </div>

                        <div class="col-finance">
                            <div class="finance-row">
                                <span class="finance-label">Số dư ví</span>
                                <b>{{ getGroupFinance(group).wallet | currency }}</b>
                            </div>
                            <div class="finance-row">
                                <span class="finance-label">Còn thiếu</span>
                                <b :class="getGroupFinance(group).payLeft > 0 ? 'red--text' : 'success--text'">
                                     {{ getGroupFinance(group).payLeft | currency }}
                                </b>
                            </div>
                            <div class="finance-row">
                                <span class="finance-label">Dư sau xuất</span>
                                <b :class="getGroupFinance(group).walletAfter >= 0 ? 'success--text' : 'red--text'">
                                    {{ getGroupFinance(group).walletAfter | currency }}
                                </b>
                            </div>
                            <v-chip
                                small
                                label
                                class="mt-2"
                                :color="getGroupFinance(group).walletAfter >= 0 ? 'green lighten-5' : 'red lighten-5'"
                                :text-color="getGroupFinance(group).walletAfter >= 0 ? 'green darken-2' : 'red darken-2'"
                            >
                                {{ getGroupFinance(group).walletAfter >= 0 ? 'Đã đủ' : 'Thiếu tiền' }}
                            </v-chip>
                        </div>

                        <div class="col-ship-note">
                            <div v-for="pkg in group.packages.slice(0, 1)" :key="'shipnote-' + pkg.ID" class="note-item">
                                <div class="caption grey--text text--darken-1 mb-1">Chỉ thị của điều vận</div>
                                <v-textarea
                                    v-model="pkg.ShippingInfo"
                                    outlined
                                    dense
                                    rows="2"
                                    hide-details
                                    :readonly="!canOperate"
                                    placeholder="Ghi chú kho..."
                                    :loading="!!shippingInfoSavingMap[pkg.ID]"
                                    @blur="canOperate && savePackageShippingInfo(pkg)"
                                ></v-textarea>
                            </div>
                        </div>

                        <div class="col-note">
                            <div v-for="pkg in group.packages.slice(0, 1)" :key="'note-' + pkg.ID" class="note-item">
                                <div class="caption grey--text text--darken-1 mb-1">{{ pkg.TransactionCode }}</div>
                                <v-textarea
                                    v-model="pkg.StoreVNNote"
                                    outlined
                                    dense
                                    rows="2"
                                    hide-details
                                    :readonly="!canOperate"
                                    placeholder="Ghi chú kho..."
                                    :loading="!!noteSavingMap[pkg.ID]"
                                    @blur="canOperate && savePackageNote(pkg)"
                                ></v-textarea>
                            </div>
                        </div>

                        <div class="col-action" v-if="canOperate">
                            <v-btn
                                small
                                color="success"
                                class="white--text text-none"
                                :disabled="isBusy || !canCreateReceiptGroup(group)"
                                @click="openReceiptDialog([group])"
                            >
                                <v-icon left small>fa-file-invoice</v-icon>
                                Tạo phiếu giao
                            </v-btn>
                        </div>
                    </div>
                </div>
            </v-card>
        </v-flex>
    </v-layout>

    <v-dialog v-model="dialogReceipt" max-width="900" persistent>
        <v-card v-if="dialogGroups.length > 0">
            <v-toolbar dark color="primary" dense elevation="0">
                <v-toolbar-title class="body-2 font-weight-bold">{{ dialogTitle }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon dark small @click="closeReceiptDialog">
                    <v-icon small>fa-times</v-icon>
                </v-btn>
            </v-toolbar>

            <div class="px-4 py-3 dialog-summary">
                <div class="mr-4">
                    <span class="caption grey--text">Khách hàng:</span>
                    <strong>{{ dialogSummary.customers }}</strong>
                </div>
                <div class="mr-4">
                    <span class="caption grey--text">Tổng kiện:</span>
                    <strong class="primary--text">{{ dialogSummary.packages }}</strong>
                </div>
                <div class="mr-4">
                    <span class="caption grey--text">Tổng cân:</span>
                    <strong>{{ dialogSummary.weight | currency('', 2) }} kg</strong>
                </div>
                <div>
                    <span class="caption grey--text">Tổng khối:</span>
                    <strong>{{ dialogSummary.volume | currency('', 2) }} CBM</strong>
                </div>
            </div>

            <v-card-text class="pa-0">
                <div style="max-height: 360px; overflow-y: auto;">
                    <table class="dialog-table">
                        <thead>
                            <tr>
                                <th>Khách hàng</th>
                                <th>Mã vận đơn</th>
                                <th>Đơn CN</th>
                                <th class="text-right">Kg</th>
                                <th class="text-right">CBM</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="pkg in dialogPackages" :key="'dialog-' + pkg.ID">
                                <td>{{ getCustomerCode(pkg.UID) }}</td>
                                <td>{{ pkg.TransactionCode }}</td>
                                <td>{{ getPackageOrderLabel(pkg) }}</td>
                                <td class="text-right">{{ pkg.Weight | currency('', 2) }}</td>
                                <td class="text-right">{{ pkg.Volume | currency('', 2) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="px-4 py-2 caption grey--text">
                    Xác nhận sẽ tạo {{ dialogGroups.length }} phiếu giao cho {{ dialogSummary.customers }} khách hàng đã chọn.
                </div>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="px-4 py-3 dialog-actions">
                <v-spacer></v-spacer>
                <v-btn outlined color="grey darken-1" class="text-none" small @click="closeReceiptDialog">Hủy</v-btn>
                <v-btn color="primary" class="text-none ml-2" :loading="creatingReceipts" small @click="confirmCreateReceipt">
                    <v-icon left small>fa-file-invoice</v-icon>
                    Tạo phiếu giao
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogShipRequest" max-width="500" persistent>
        <v-card v-if="activeShipGroup">
            <v-toolbar dark color="primary" dense elevation="0">
                <v-toolbar-title class="body-2 font-weight-bold">
                    Yêu cầu ship — Khách hàng {{ activeShipGroup.UserCode }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon dark small @click="closeShipRequestDialog">
                    <v-icon small>fa-times</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pa-4">
                <div class="subtitle-2 font-weight-bold mb-2">Bạn có NOTE gì không?</div>
                <v-textarea
                    v-model="shipNoteInput"
                    outlined
                    dense
                    rows="4"
                    placeholder="Ghi chú của điều vận..."
                    hide-details
                ></v-textarea>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="px-4 py-3">
                <v-spacer></v-spacer>
                <v-btn outlined color="grey darken-1" class="text-none" small @click="closeShipRequestDialog">Hủy</v-btn>
                <v-btn color="primary" class="text-none ml-2" small :loading="savingShipRequest" @click="confirmShipRequest">
                    <v-icon left small>fa-check</v-icon>
                    Xác nhận
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</v-container>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";
import { sendErrorNotice, sendSuccessNotice } from "@/commons/utils";
import configs from "@/configs";

export default {
    data() {
        return {
            searchQuery: "",
            selectedCustomerId: "all",
            selectedCustomerUIDs: [],
            filterMode: "all",
            showPhoneMap: {},
            dialogReceipt: false,
            dialogGroups: [],
            creatingReceipts: false,
            dialogShipRequest: false,
            activeShipGroup: null,
            shipNoteInput: "",
            savingShipRequest: false,
            shippingInfoSavingMap: {},
            noteSavingMap: {},
            tableConfig: {
                pagination: { itemsPerPage: -1, sortBy: ["ID"] },
                filter: [],
                quickFilter: []
            },
            payOrderTableConfig: {
                pagination: { itemsPerPage: -1, sortBy: ["ID"], sortDesc: [true] },
                filter: [],
                quickFilter: []
            }
        };
    },
    watch: {
        filteredGroupedDeliverables: {
            handler(groups) {
                const visibleIds = groups.map(group => group.UID);
                this.selectedCustomerUIDs = this.selectedCustomerUIDs.filter(uid => visibleIds.includes(uid));
            },
            immediate: true
        }
    },
    computed: {
        ...mapState({
            items: state => (state.deliverable.all && state.deliverable.all.data) || [],
            deliverableLoading: state => state.deliverable.loading,
            customers: state => (state.customer.master && state.customer.master.data) || [],
            payOrderCustomers: state => state.payOrder.all || []
        }),
        isBusy() {
            return this.deliverableLoading || this.creatingReceipts;
        },
        groupedDeliverables() {
            const validPackages = (this.items || []).filter(pkg => (pkg.Status == 4 || !pkg.Status) && (!pkg.ReceiptID || pkg.ReceiptID == 0 || pkg.ReceiptID == '0'));
            const groups = {};

            validPackages.forEach(pkg => {
                const uid = pkg.UID;
                if (!uid) return;

                if (!groups[uid]) {
                    groups[uid] = {
                        UID: uid,
                        UserCode: pkg.UserCode,
                        Username: pkg.Username,
                        packages: [],
                        orders: new Set(),
                        transactionCodes: new Set(),
                        Weight: 0,
                        Volume: 0,
                        totalPackages: 0
                    };
                }

                groups[uid].packages.push(pkg);
                groups[uid].orders.add(this.getPackageOrderLabel(pkg));
                groups[uid].transactionCodes.add(pkg.TransactionCode);
                groups[uid].Weight += parseFloat(pkg.Weight) || 0;
                groups[uid].Volume += parseFloat(pkg.Volume) || 0;
                groups[uid].totalPackages += parseInt(pkg.NumberOfPack) || 1;
            });

            return Object.values(groups).map(group => {
                let hasShipRequest = false;
                let shipRequestDate = "";
                let firstShippingInfo = "";

                group.packages.forEach(pkg => {
                    if (pkg.ShippingMethod && pkg.ShippingMethod.startsWith("YEU_CAU_SHIP")) {
                        hasShipRequest = true;
                        const parts = pkg.ShippingMethod.split("|");
                        if (parts.length > 1 && !shipRequestDate) {
                            shipRequestDate = parts[1];
                        }
                    }
                    if (pkg.ShippingInfo && !firstShippingInfo) {
                        firstShippingInfo = pkg.ShippingInfo;
                    }
                });

                return {
                    ...group,
                    hasShipRequest,
                    shipRequestDate,
                    firstShippingInfo,
                    orders: Array.from(group.orders),
                    transactionCodes: Array.from(group.transactionCodes)
                };
            });
        },
        customerFilterItems() {
            return [
                { text: "Tất cả khách hàng", value: "all" },
                ...this.groupedDeliverables.map(group => ({
                    text: `${group.UserCode} - ${this.getCustomerFullName(group.UID)}`,
                    value: group.UID
                }))
            ];
        },
        filteredGroupedDeliverables() {
            let groups = this.groupedDeliverables;

            if (this.filterMode === "ship_requested") {
                groups = groups.filter(group => group.hasShipRequest);
            }

            if (this.selectedCustomerId !== "all") {
                groups = groups.filter(group => group.UID === this.selectedCustomerId);
            }

            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                groups = groups.filter(group => {
                    const fullName = this.getCustomerFullName(group.UID).toLowerCase();
                    const userCode = (group.UserCode || "").toLowerCase();
                    const hasPackageMatch = group.packages.some(pkg => (pkg.TransactionCode || "").toLowerCase().includes(query));
                    const hasOrderMatch = group.orders.some(orderCode => orderCode.toLowerCase().includes(query));
                    return fullName.includes(query) || userCode.includes(query) || hasPackageMatch || hasOrderMatch;
                });
            }

            return groups;
        },
        isAllSelected() {
            return this.filteredGroupedDeliverables.length > 0 && this.selectedCustomerUIDs.length === this.filteredGroupedDeliverables.length;
        },
        selectedGroups() {
            return this.filteredGroupedDeliverables.filter(group => this.selectedCustomerUIDs.includes(group.UID));
        },
        selectedSummary() {
            return this.buildGroupSummary(this.selectedGroups);
        },
        summaryStats() {
            const baseSummary = this.buildGroupSummary(this.groupedDeliverables);
            const shipRequestedCustomers = this.groupedDeliverables.filter(g => g.hasShipRequest).length;
            return {
                ...baseSummary,
                shipRequestedCustomers
            };
        },
        dialogTitle() {
            if (this.dialogGroups.length === 1) {
                return `Tạo phiếu giao · Khách hàng ${this.dialogGroups[0].UserCode}`;
            }
            return `Tạo phiếu giao · ${this.dialogGroups.length} khách hàng`;
        },
        dialogPackages() {
            return _.flatMap(this.dialogGroups, group => group.packages.map(pkg => ({ ...pkg, UID: group.UID })));
        },
        dialogSummary() {
            return this.buildGroupSummary(this.dialogGroups);
        },
        hasAnyValidReceiptGroup() {
            return this.filteredGroupedDeliverables.some(group => this.canCreateReceiptGroup(group));
        },
        canViewCustomerPhone() {
            const user = (this.$store.state.authentication && this.$store.state.authentication.user) || {};
            const roletype = Number(user.roletype) || 0;
            const isAdmin = (roletype & configs.ROLE_TYPE.Admin) == configs.ROLE_TYPE.Admin;
            const isStoreVN = (roletype & configs.ROLE_TYPE.StoreVN) == configs.ROLE_TYPE.StoreVN;
            const isStoreTQ = (roletype & configs.ROLE_TYPE.StoreTQ) == configs.ROLE_TYPE.StoreTQ;
            const isStoreTQ2 = (roletype & configs.ROLE_TYPE.StoreTQ2) == configs.ROLE_TYPE.StoreTQ2;
            return isAdmin || isStoreVN || isStoreTQ || isStoreTQ2;
        },
        canOperate() {
            const user = (this.$store.state.authentication && this.$store.state.authentication.user) || {};
            const roletype = Number(user.roletype) || 0;
            const isAdmin = (roletype & configs.ROLE_TYPE.Admin) == configs.ROLE_TYPE.Admin;
            const isPayment = (roletype & configs.ROLE_TYPE.Payment) == configs.ROLE_TYPE.Payment;
            const isManager = (roletype & configs.ROLE_TYPE.Manager) == configs.ROLE_TYPE.Manager;
            const isStoreVN = (roletype & configs.ROLE_TYPE.StoreVN) == configs.ROLE_TYPE.StoreVN;
            return isAdmin || isManager || isPayment || isStoreVN;
        }
    },
    methods: {
        refreshData() {
            this.filter_data();
            this.getPayOrderSummary();
            this.getCustomerList();
        },
        filter_data() {
            this.$store.dispatch("deliverable/getList", this.tableConfig);
        },
        getCustomerList() {
            this.$store.dispatch("customer/getCustomerList", {
                is_master: 1,
                fields: ["ID", "Username", "UserCode", "FirstName", "LastName", "Wallet", "FinanceApproved", "PhonePrefix", "PhoneNumber", "Address"]
            });
        },
        getPayOrderSummary() {
            this.$store.dispatch("payOrder/getPayOrderList", this.payOrderTableConfig);
        },
        getCustomerData(uid) {
            return _.find(this.customers, { ID: uid });
        },
        getCustomerCode(uid) {
            const customer = this.getCustomerData(uid);
            return customer ? customer.UserCode : "";
        },
        getCustomerFullName(uid) {
            const customer = this.getCustomerData(uid);
            if (customer) {
                const firstName = customer.FirstName || "";
                const lastName = customer.LastName || "";
                return (lastName + " " + firstName).trim() || customer.Username || customer.UserCode;
            }
            const payOrderCustomer = _.find(this.payOrderCustomers, { ID: uid });
            return payOrderCustomer ? payOrderCustomer.CustomerName : "Unknown";
        },
        getCustomerAddress(uid) {
            const customer = this.getCustomerData(uid);
            return customer && customer.Address ? customer.Address.trim() : "";
        },
        getCustomerPhone(uid) {
            const customer = this.getCustomerData(uid);
            if (!customer) return "Chưa có SĐT";
            const prefix = customer.PhonePrefix || "";
            const phone = customer.PhoneNumber || "";
            const fullPhone = `${prefix}${phone}`.trim();
            return fullPhone || "Chưa có SĐT";
        },
        toggleShowPhone(uid) {
            const phone = this.getCustomerPhone(uid);
            this.$set(this.showPhoneMap, uid, !this.showPhoneMap[uid]);
            if (this.showPhoneMap[uid]) {
                sendSuccessNotice(`SĐT KH ${this.getCustomerCode(uid)}: ${phone}`);
            }
        },
        getCustomerWallet(uid) {
            const customer = this.getCustomerData(uid);
            if (customer && customer.Wallet !== undefined) {
                return Number(customer.Wallet) || 0;
            }
            const payOrderCustomer = _.find(this.payOrderCustomers, { ID: uid });
            return payOrderCustomer ? (Number(payOrderCustomer.Wallet) || 0) : 0;
        },
        getGroupFinance(group) {
            const payOrderCustomer = _.find(this.payOrderCustomers, { ID: group.UID });
            const wallet = this.getCustomerWallet(group.UID);
            const payLeft = payOrderCustomer ? (Number(payOrderCustomer.PayLeft) || 0) : 0;
            return {
                wallet,
                payLeft,
                walletAfter: wallet - payLeft
            };
        },
        getPackageOrderLabel(pkg) {
            return `${pkg.OrderType == 1 ? "MD" : "KG"}${pkg.OrderID}`;
        },
        getPackageOrderLink(pkg) {
            return `${pkg.OrderType == 1 ? "/don-hang/" : "/ky-gui/"}${pkg.OrderID}`;
        },
        buildGroupSummary(groups) {
            const summary = {
                customers: groups.length,
                packages: 0,
                transactions: 0,
                weight: 0,
                volume: 0
            };
            const transactionSet = new Set();

            groups.forEach(group => {
                summary.packages += group.totalPackages || 0;
                summary.weight += group.Weight || 0;
                summary.volume += group.Volume || 0;
                group.transactionCodes.forEach(code => transactionSet.add(code));
            });

            summary.transactions = transactionSet.size;
            return summary;
        },
        toggleSelectAll(checked) {
            this.selectedCustomerUIDs = checked ? this.filteredGroupedDeliverables.map(group => group.UID) : [];
        },
        toggleSelectGroup(uid, checked) {
            if (checked) {
                if (!this.selectedCustomerUIDs.includes(uid)) {
                    this.selectedCustomerUIDs = [...this.selectedCustomerUIDs, uid];
                }
            } else {
                this.selectedCustomerUIDs = this.selectedCustomerUIDs.filter(item => item !== uid);
            }
        },
        isGroupSelected(uid) {
            return this.selectedCustomerUIDs.includes(uid);
        },
        canCreateReceiptGroup(group) {
            const customer = this.getCustomerData(group.UID);
            return !!customer && customer.FinanceApproved !== 0 && !!group.hasShipRequest;
        },
        validateGroupsForReceipt(groups) {
            if (!groups || groups.length === 0) {
                sendErrorNotice("Không có khách hàng nào để tạo phiếu giao.");
                return false;
            }
            const blockedFinanceGroups = groups.filter(group => {
                const customer = this.getCustomerData(group.UID);
                return !customer || customer.FinanceApproved === 0;
            });
            if (blockedFinanceGroups.length > 0) {
                sendErrorNotice(`Khách ${blockedFinanceGroups.map(group => group.UserCode).join(", ")} chưa được duyệt tài chính.`);
                return false;
            }
            const blockedShipGroups = groups.filter(group => !group.hasShipRequest);
            if (blockedShipGroups.length > 0) {
                sendErrorNotice(`Khách ${blockedShipGroups.map(group => group.UserCode).join(", ")} chưa được tạo yêu cầu ship.`);
                return false;
            }
            return true;
        },
        openReceiptDialog(groups) {
            if (!this.validateGroupsForReceipt(groups)) return;
            this.dialogGroups = groups;
            this.dialogReceipt = true;
        },
        openSelectedReceiptDialog() {
            const validGroups = this.selectedGroups.filter(group => this.canCreateReceiptGroup(group));
            if (validGroups.length === 0) {
                sendErrorNotice("Các khách hàng đã chọn chưa tạo yêu cầu ship hoặc chưa được duyệt tài chính.");
                return;
            }
            this.openReceiptDialog(validGroups);
        },
        openAllReceiptDialog() {
            const validGroups = this.filteredGroupedDeliverables.filter(group => this.canCreateReceiptGroup(group));
            if (validGroups.length === 0) {
                sendErrorNotice("Không có khách hàng nào thỏa mãn điều kiện (đã tạo yêu cầu ship & đã duyệt tài chính) để tạo phiếu.");
                return;
            }
            this.openReceiptDialog(validGroups);
        },
        closeReceiptDialog() {
            this.dialogReceipt = false;
            this.dialogGroups = [];
        },
        async confirmCreateReceipt() {
            if (!this.validateGroupsForReceipt(this.dialogGroups)) return;

            this.creatingReceipts = true;
            let successCount = 0;
            const createdReceiptIds = [];

            try {
                for (const group of this.dialogGroups) {
                    const customer = this.getCustomerData(group.UID);
                    const res = await this.$store.dispatch("deliverable/createReceipt", {
                        uid: group.UID,
                        packageIds: group.packages.map(pkg => pkg.ID),
                        customerInfo: {
                            Username: customer.Username || customer.UserCode,
                            Receiver: this.getCustomerFullName(customer.ID),
                            Phone: `${customer.PhonePrefix || ""}${customer.PhoneNumber || ""}`,
                            Address: customer.Address || ""
                        },
                        refreshList: false,
                        notifySuccess: false
                    });
                    if (res && res.data && res.data.ID) {
                        createdReceiptIds.push(res.data.ID);
                    }
                    successCount += 1;
                }

                sendSuccessNotice(successCount > 1 ? `Đã tạo ${successCount} phiếu xuất kho thành công.` : "Tạo phiếu xuất kho thành công.");
                this.closeReceiptDialog();
                this.selectedCustomerUIDs = [];
                this.refreshData();

                if (createdReceiptIds.length > 0) {
                    this.$router.push({ name: 'ExportReceipt', query: { id: createdReceiptIds[0] } });
                }
            } catch (err) {
                if (successCount > 0) {
                    sendErrorNotice(`Đã tạo ${successCount} phiếu trước khi dừng do lỗi.`);
                    this.refreshData();
                    if (createdReceiptIds.length > 0) {
                        this.$router.push({ name: 'ExportReceipt', query: { id: createdReceiptIds[0] } });
                    }
                }
            } finally {
                this.creatingReceipts = false;
            }
        },
        async savePackageNote(pkg) {
            this.$set(this.noteSavingMap, pkg.ID, true);
            try {
                await this.$store.dispatch("deliverable/setDetail", {
                    data: {
                        ID: pkg.ID,
                        StoreVNNote: pkg.StoreVNNote
                    },
                    param: null,
                    notifySuccess: false
                });
            } catch (err) {
                // Notice already handled in store.
            } finally {
                this.$delete(this.noteSavingMap, pkg.ID);
            }
        },
        async savePackageShippingInfo(pkg) {
            this.$set(this.shippingInfoSavingMap, pkg.ID, true);
            try {
                await this.$store.dispatch("deliverable/setDetail", {
                    data: {
                        ID: pkg.ID,
                        ShippingInfo: pkg.ShippingInfo
                    },
                    param: null,
                    notifySuccess: false
                });
            } catch (err) {
                // Notice already handled in store.
            } finally {
                this.$delete(this.shippingInfoSavingMap, pkg.ID);
            }
        },
        setFilterMode(mode) {
            this.filterMode = mode;
        },
        openShipRequestDialog(group) {
            this.activeShipGroup = group;
            this.shipNoteInput = group.firstShippingInfo || "";
            this.dialogShipRequest = true;
        },
        closeShipRequestDialog() {
            this.dialogShipRequest = false;
            this.activeShipGroup = null;
            this.shipNoteInput = "";
        },
        async confirmShipRequest() {
            if (!this.activeShipGroup) return;
            this.savingShipRequest = true;

            const now = new Date();
            const day = String(now.getDate()).padStart(2, "0");
            const month = String(now.getMonth() + 1).padStart(2, "0");
            const year = now.getFullYear();
            const hours = String(now.getHours()).padStart(2, "0");
            const minutes = String(now.getMinutes()).padStart(2, "0");
            const seconds = String(now.getSeconds()).padStart(2, "0");
            const formattedDate = `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
            const shippingMethodValue = `YEU_CAU_SHIP|${formattedDate}`;

            try {
                for (const pkg of this.activeShipGroup.packages) {
                    pkg.ShippingInfo = this.shipNoteInput;
                    pkg.ShippingMethod = shippingMethodValue;
                    await this.$store.dispatch("deliverable/setDetail", {
                        data: {
                            ID: pkg.ID,
                            ShippingInfo: this.shipNoteInput,
                            ShippingMethod: shippingMethodValue
                        },
                        param: null,
                        notifySuccess: false
                    });
                }
                sendSuccessNotice("Đã lưu yêu cầu ship thành công.");
                this.closeShipRequestDialog();
                this.refreshData();
            } catch (err) {
                sendErrorNotice("Không thể lưu yêu cầu ship.");
            } finally {
                this.savingShipRequest = false;
            }
        }
    },
    mounted() {
        this.getCustomerList();
        this.getPayOrderSummary();
        this.filter_data();
        this.$root.$on("REQUEST_REFRESH_DATA", this.refreshData);
    },
    beforeDestroy() {
        this.$root.$off("REQUEST_REFRESH_DATA", this.refreshData);
    }
};
</script>

<style scoped>
.deliverable-container {
    color: #1f2937;
}

.stat-card {
    border: 1px solid #e5e7eb;
}

.deliverable-toolbar {
    border-bottom: 1px solid #e5e7eb;
}

.deliverable-toolbar-actions {
    margin-top: 8px;
}

.selection-bar {
    background: #eff6ff;
    border-bottom: 1px solid #bfdbfe;
}

.selection-text {
    font-size: 13px;
    color: #1d4ed8;
}

.deliverable-table-header,
.group-row {
    display: flex;
    align-items: flex-start;
}

.deliverable-table-header {
    background: #f3f4f6;
    border-bottom: 1px solid #d1d5db;
    font-size: 12px;
    font-weight: 600;
    color: #4b5563;
}

.group-row {
    border-bottom: 1px solid #e5e7eb;
}

.deliverable-table-header > div,
.group-row > div {
    padding: 12px;
}

.col-select {
    width: 48px;
    min-width: 48px;
    display: flex;
    justify-content: center;
    padding-top: 18px;
}

.col-customer {
    width: 220px;
    min-width: 220px;
}

.col-packages {
    flex: 1.2;
    min-width: 280px;
}

.col-finance {
    width: 220px;
    min-width: 220px;
}

.col-ship-note {
    flex: 1;
    min-width: 260px;
}

.col-note {
    flex: 1;
    min-width: 260px;
}

.stat-card.cursor-pointer {
    cursor: pointer;
    transition: all 0.2s ease;
}

.stat-card.cursor-pointer:hover {
    border-color: #2563eb;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.15);
}

.stat-card-active {
    border-color: #2563eb !important;
    background-color: #eff6ff !important;
}

.ship-request-info {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 4px 8px;
}

.col-action {
    width: 160px;
    min-width: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.summary-pill {
    display: inline-block;
    background: #eff6ff;
    color: #1d4ed8;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 12px;
    margin-right: 6px;
    margin-bottom: 6px;
}

.package-item,
.note-item {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 8px;
    margin-bottom: 8px;
    background: #fff;
}

.package-code {
    font-family: monospace;
    font-weight: 700;
    color: #2563eb;
    margin-bottom: 4px;
}

.package-meta {
    font-size: 12px;
    color: #6b7280;
}

.finance-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    margin-bottom: 6px;
}

.finance-label {
    color: #6b7280;
    margin-right: 12px;
}

.dialog-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
}

.dialog-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.dialog-table thead tr {
    position: sticky;
    top: 0;
    background: #eff6ff;
    z-index: 1;
}

.dialog-table th,
.dialog-table td {
    padding: 8px 10px;
    border-top: 1px solid #e5e7eb;
    text-align: left;
}

.dialog-actions {
    background: #f8fafc;
}

@media (max-width: 1264px) {
    .deliverable-toolbar-actions {
        text-align: left !important;
        margin-top: 12px;
    }
}

@media (max-width: 960px) {
    .deliverable-table {
        overflow-x: auto;
    }
}
</style>
